import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import { Table, Divider } from 'antd';

import { Tick as TickIcon, TickWrong as TickWrongIcon } from '~components/svgs';

const useStyles = makeStyles(theme => ({
  table: {
    border: '1px solid #e8e8e8',
    borderBottom: 'none',
  },
  desc: {
    ...theme.typography.body3,
    fontWeight: 400,
    marginLeft: '8px',
  },

  guarantor: {
    whiteSpace: 'pre-wrap',
    '& span': {
      whiteSpace: 'pre-wrap',
      display: 'inline-block',
    },
    '& svg': {
      verticalAlign: '-3px',
    },
  },
  country: {
    ...theme.typography.h5,
    whiteSpace: 'pre-wrap',
    fontWeight: 400,
    color: theme.colors.captionColor,
    display: 'inline!important',
  },
  icon: {
    verticalAlign: '-3px',
  },
}));

const RentPaymentTable = ({ dataSource, paymentOnline }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const generateColumns = useMemo(() => {
    return [
      {
        title: t('landlord.payment.long_tail.rent_payment.table.from_tenancy'),
        width: '18%',
        dataIndex: 'fromTenancy',
        key: 'fromTenancy',
        render: (_, record, index) => {
          if (dataSource?.length === 1) {
            return t('landlord.payment.long_tail.rent_payment.table.all');
          } else if (index === 0) {
            return '0';
          } else {
            return `> ${record.fromTenancy}`;
          }
        },
      },
      {
        title: t('landlord.payment.long_tail.rent_payment.table.to_tenancy'),
        width: '18%',
        dataIndex: 'toTenancy',
        key: 'toTenancy',
        render: (_, record, index) => {
          if (index === dataSource.length - 1) {
            return '-';
          } else {
            return '≤ ' + record.toTenancy;
          }
        },
      },
      {
        title: t('landlord.payment.long_tail.rent_payment.table.pay_now_to_student'),
        width: '21%',
        dataIndex: 'payNowAmount',
        key: 'payNowAmount',
        render: (text, record) => {
          const tenancyUnit = record.tenancyUnit === 'WEEKS' ? 'week' : 'month';
          // paymentOnline有三种值null，true，false，用false判断
          if (!paymentOnline) {
            return '-';
          }
          if (record.fullPayment && !record.payNowAmount) {
            return t('landlord.payment.long_tail.full_payment.field');
          } else if (record.payNowAmount && !record.fullPayment) {
            return (
              text +
              ' ' +
              t(
                `landlord.payment.long_tail.rent_payment.setting_modal.${
                  record.payNowAmount > 1
                    ? t(`landlord.payment.long_tail.rent_payment.setting_modal.${tenancyUnit}s`)
                    : t(`landlord.payment.long_tail.rent_payment.setting_modal.${tenancyUnit}`)
                }`,
              )
            );
          } else if (record.fullPayment && record.payNowAmount) {
            return `${t('landlord.payment.long_tail.full_payment.field')} / ${text} ${
              record.payNowAmount > 1
                ? t(`landlord.payment.long_tail.rent_payment.setting_modal.${tenancyUnit}s`)
                : t(`landlord.payment.long_tail.rent_payment.setting_modal.${tenancyUnit}`)
            }`;
          } else {
            return '-';
          }
        },
      },
      {
        title: t('landlord.payment.long_tail.rent_payment.table.pay_late_directly'),
        width: '21%',
        render: (_, record) => {
          if (
            paymentOnline &&
            ((record.fullPayment && !record.payNowAmount) ||
              (!record.payNowAmount && !record.fullPayment))
          ) {
            return '-';
          } else {
            const result = [];
            if (!paymentOnline && record.fullPayment) {
              result.push(t('landlord.payment.long_tail.rent_payment.setting_modal.full_payment'));
            }
            if (paymentOnline && record.payRemainderAtOnce) {
              result.push(t('landlord.payment.long_tail.rent_payment.setting_modal.all_at_once'));
            }

            if (record.payQuarterly) {
              result.push(t('landlord.payment.long_tail.rent_payment.setting_modal.quarterly'));
            }

            if (record.payMonthly) {
              result.push(t('landlord.payment.long_tail.rent_payment.setting_modal.monthly'));
            }

            if (record.payWeekly) {
              result.push(t('landlord.payment.long_tail.rent_payment.setting_modal.weekly'));
            }

            return result.join(', ');
          }
        },
      },
      {
        title: t(
          'landlord.payment.long_tail.rent_payment.table.installment_requires_guarantor_info',
        ),
        width: '22%',
        dataIndex: 'requireGuarantor',
        key: 'requireGuarantor',
        render: (_, record) => {
          if (
            paymentOnline &&
            ((record.fullPayment && !record.payNowAmount) ||
              (!record.payNowAmount && !record.fullPayment))
          ) {
            return '-';
          } else if (record.requireGuarantor) {
            return (
              <div className={classes.guarantor}>
                <TickIcon style={{ width: '16px', height: '16px', fill: '#38B2A6' }} />
                <span className={classes.desc}>Yes</span>
                <Divider type="vertical" />
                <span className={classes.country}>
                  {record?.country !== null ? record?.country?.name : 'Not Specific'}
                </span>
              </div>
            );
          } else {
            return (
              <div>
                <TickWrongIcon className={classes.icon} />
                <span className={classes.desc}>No</span>
              </div>
            );
          }
        },
      },
    ];
  }, [dataSource]);

  return (
    <Table
      columns={generateColumns}
      dataSource={dataSource}
      pagination={false}
      className={`${classes.table} long-tail__table long-tail__table--installment`}
    />
  );
};

export default RentPaymentTable;
